import React from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { MetricId, getMetric, getMetricScore } from './metricConfig';
import { MeasurementResults } from '../../hooks/useResultsExport';
import MetricGauge from './MetricGauge';
import { markerPosition } from './scoreBands';
import DetailBlocks from './DetailBlocks';
import { buildMetricBlocks } from './metricDetailBlocks';
import Logo from '../Logo';
import FooterLogo from '../FooterLogo';
import { colors, fontSize, fontWeight } from '../../style/tokens';
import { useTheme } from 'styled-components';
import { headerBg } from '../../style/header';

interface MetricDetailPageProps {
  metricId: MetricId;
  results: MeasurementResults;
  onBack: () => void;
}

// ── Layout ────────────────────────────────────────────────────────────────────

const Page = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background: var(--sfondo, ${colors.pageBg});
  font-family: 'Outfit', sans-serif;
  display: flex;
  flex-direction: column;
`;

// ── Top bar ───────────────────────────────────────────────────────────────────

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px 26px;
  min-height: 82px;
  background: ${headerBg};
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
  position: relative;
`;

const BackButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1.5px solid ${p => p.theme.coloreBottoneInattivo || colors.border};
  background: ${colors.bg};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
  @media (hover: hover) { &:hover { background: ${p => p.theme.coloreBottoneAttivo || '#f5f5f5'}; border-color: transparent; } }
  &::before {
    content: '';
    display: block;
    width: 9px;
    height: 9px;
    border-top: 2px solid #333;
    border-left: 2px solid #333;
    transform: rotate(-45deg) translate(2px, 0);
  }
`;

const LogoCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
`;

const Spacer = styled.span`width: 42px;`;

// ── Hero ──────────────────────────────────────────────────────────────────────

const Hero = styled.div`
  background: ${({ theme }) => theme.coloreSfondoIndicatore || theme.gradient1};
  padding: 20px 24px 22px;
  flex-shrink: 0;
`;

const HeroHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
`;

const HeroIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${fontSize.xl2};
  flex-shrink: 0;
  overflow: hidden;
`;

const HeroTitle = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: #fff;
  line-height: 1.15;
`;

const HeroSub = styled.p`
  font-size: ${fontSize.base};
  color: rgba(255, 255, 255, 0.82);
  padding-left: 2px;
`;

// ── Gauge bar ─────────────────────────────────────────────────────────────────

const GaugeBar = styled.div`
  padding: 18px 24px 16px;
  background: ${colors.bg};
  border-bottom: 1px solid #f0f0f0;
`;

// ── Bottom actions ────────────────────────────────────────────────────────────

const BtnRow = styled.div`
  padding: 20px 14px 24px;
  display: flex;
  gap: 10px;
  flex-shrink: 0;
`;

const BtnGhost = styled.button`
  flex: 1;
  padding: 15px 0;
  border-radius: 50px;
  border: 1.5px solid ${p => p.theme.coloreBottoneInattivo || '#ddd'};
  background: ${colors.bg};
  font-size: 15px;
  font-weight: ${fontWeight.medium};
  font-family: 'Outfit', sans-serif;
  color: ${p => p.theme.coloreBottoneInattivo || '#555'};
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  @media (hover: hover) {
    &:hover {
      background: ${p => p.theme.coloreBottoneAttivo || '#f9f9f9'};
      border-color: transparent;
      color: white;
    }
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

const MetricDetailPage: React.FC<MetricDetailPageProps> = ({
  metricId,
  results,
  onBack,
}) => {
  const intl = useIntl();
  const { metricIcons, resultBands } = useTheme();
  const config = getMetric(metricId);
  const iconUrl = metricIcons[metricId] || config.icon;
  const demographics = results.demographics;
  const score = getMetricScore(config, results, demographics);
  const available = score !== null;
  // §4.5/§4.9 band on a raw value, not the score (Rule 1) — null elsewhere.
  const rawValue = config.getBandValue?.(results, demographics) ?? null;
  const blocks = buildMetricBlocks(config, results, score, intl);

  return (
    <Page>
      <TopBar>
        <BackButton onClick={onBack} aria-label="Back to report" />
        <LogoCenter><Logo /></LogoCenter>
        <Spacer />
      </TopBar>

      <Hero>
        <HeroHead>
          {(iconUrl || config.emoji) && (
            <HeroIcon>
              {iconUrl
                ? <img src={iconUrl} alt="" style={{ width: 38, height: 38, objectFit: 'contain', display: 'block' }} />
                : config.emoji}
            </HeroIcon>
          )}
          <HeroTitle>{intl.formatMessage({ id: config.labelKey })}</HeroTitle>
        </HeroHead>
        <HeroSub>
          {available
            ? intl.formatMessage({ id: config.getDescriptionKey(score!, rawValue) })
            : intl.formatMessage({ id: 'metric.ui.unavailable' })}
        </HeroSub>
      </Hero>

      {available && (
        <GaugeBar>
          <MetricGauge
            position={markerPosition(config.id, score!, resultBands, rawValue)}
            labels={config.gaugeKeys.map(k => intl.formatMessage({ id: k })) as [string, string, string]}
            size="large"
            bands={resultBands}
          />
        </GaugeBar>
      )}

      <DetailBlocks blocks={blocks} />

      <BtnRow>
        <BtnGhost onClick={onBack}>{intl.formatMessage({ id: 'metric.ui.backToReport' })}</BtnGhost>
      </BtnRow>

      <FooterLogo />
    </Page>
  );
};

export default MetricDetailPage;
